/**
 * @fileoverview POD Claim Panel — slide-over detail for one Proof of Debt
 * claim: lifecycle actions, claim particulars vs substantiated line items,
 * the deterministic reconciliation, and the adjudication form mirroring
 * the practitioner's internal Proof of Debt Adjudication Form.
 */

import { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  convexQuery,
  useConvexAction,
  useConvexMutation,
} from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/ui/composed/overlays/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/ui/composed/sections/tabs";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Checkbox } from "@/ui/primitives/checkbox";
import { Input } from "@/ui/primitives/input";
import { Textarea } from "@/ui/primitives/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  FileText,
  Gavel,
  GitMerge,
  History,
  Info,
  Landmark,
  Mail,
  Search,
  StickyNote,
  Loader2,
  Plus,
  Trash2,
  Undo2,
  Upload,
  UserRound,
  Wand2,
  X,
} from "lucide-react";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";
import { RelatedPartyBadge } from "../_shared/RelatedPartyBadge";
import {
  POD_CREDITOR_TYPE_COLORS,
  POD_CREDITOR_TYPE_LABELS,
  POD_RECOMMENDATION_LABELS,
  POD_STATUS_COLORS,
  POD_STATUS_LABELS,
  POD_SUPPORTING_DOC_LABELS,
  LIABILITY_NATURE_LABELS,
  LIABILITY_SOURCE_LABELS,
  derivePodOutcome,
  fmtDate,
  fmtMoney,
  fmtTimestamp,
  type AdjudicationDraft,
  type PodClaimDetailUI,
  type PodClaimItemKind,
  type PodClaimItemUI,
  type PodClaimUI,
  type PodCreditorType,
  type PodCrossCheckPaymentUI,
  type PodRecommendation,
  type PodSupportingDocType,
} from "./podShared";
import {
  POD_CLAIM_FIELD_FORMATTERS,
  POD_CLAIM_FIELD_LABELS,
} from "./historyFormatters";
import {
  ChangeHistory,
  type ResolvedHistoryUser,
} from "../gather/summarization/shared-panels/ChangeHistory";
import { PodClaimNotesFeed } from "./PodClaimNotesFeed";
import { ClaimEvidenceDialog } from "./ClaimEvidenceDialog";
import { PodClaimExportMenu } from "./PodClaimExportMenu";
import { AiDisclaimer } from "@/ui/composed/ai-disclaimer";

const LABEL_CLASS =
  "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground";

/**
 * Sentinels for the source-file select. Radix rejects an empty option
 * value, so "no file" needs a name of its own, and the last option opens
 * the evidence dialog rather than selecting anything.
 */
const NO_SOURCE_FILE = "__none__";
const ADD_SOURCE_FILE = "__add__";

/** Rounds to the cent for display comparisons. */
const roundTo2 = (v: number) => Math.round(v * 100) / 100;

export function PodClaimPanel({
  claimId,
  claims,
  onClose,
  onViewDocument,
  onViewContact,
  onMerged,
}: {
  claimId: Id<"podClaims"> | null;
  /** Every claim on the job — the merge target candidates. */
  claims: PodClaimUI[];
  onClose: () => void;
  /** Called with the surviving claim after a merge. */
  onMerged: (targetId: Id<"podClaims">) => void;
  /**
   * Opens the creditor's full contact record. Handled by the parent for the
   * same reason as the document viewer — the contact panel is a Sheet, and
   * one nested inside this Dialog renders behind its overlay.
   */
  onViewContact: (contactId: Id<"jobContacts">) => void;
  /**
   * Opens a source document. Handled by the parent so the document sheet
   * mounts as a sibling of this one — a Radix Sheet nested inside another
   * Sheet renders behind its parent's overlay.
   */
  onViewDocument: (documentId: Id<"documents">) => void;
}) {
  return (
    <Dialog open={claimId !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex h-[88vh] max-w-5xl flex-row gap-0 overflow-hidden p-0">
        {/* The loader owns the query, so it is only ever issued with a real
            claim id — mounting it unconditionally would fire the query with
            a placeholder and fail arg validation. */}
        {claimId !== null && (
          <PodClaimDetailLoader
            key={claimId}
            claimId={claimId}
            claims={claims}
            onClose={onClose}
            onViewDocument={onViewDocument}
            onViewContact={onViewContact}
            onMerged={onMerged}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function PodClaimDetailLoader({
  claimId,
  claims,
  onClose,
  onViewDocument,
  onViewContact,
  onMerged,
}: {
  claimId: Id<"podClaims">;
  claims: PodClaimUI[];
  onClose: () => void;
  onViewDocument: (documentId: Id<"documents">) => void;
  onViewContact: (contactId: Id<"jobContacts">) => void;
  onMerged: (targetId: Id<"podClaims">) => void;
}) {
  const { data: detail } = useQuery(
    convexQuery(api.jobs.recoveries.publicQueries.getPodClaimDetail, {
      claimId,
    }),
  );

  if (!detail) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <PodClaimDetail
      detail={detail as PodClaimDetailUI}
      claims={claims}
      onClose={onClose}
      onViewDocument={onViewDocument}
      onViewContact={onViewContact}
      onMerged={onMerged}
    />
  );
}

// ---------------------------------------------------------------------------
// Detail body
// ---------------------------------------------------------------------------

function PodClaimDetail({
  detail,
  claims,
  onClose,
  onViewDocument,
  onViewContact,
  onMerged,
}: {
  detail: PodClaimDetailUI;
  claims: PodClaimUI[];
  onClose: () => void;
  onViewDocument: (documentId: Id<"documents">) => void;
  onViewContact: (contactId: Id<"jobContacts">) => void;
  onMerged: (targetId: Id<"podClaims">) => void;
}) {
  const { claim, claimedItems, substantiatedItems } = detail;
  const outcome = derivePodOutcome(claim);
  // The detail query returns the claim document; relatedness is derived, so it
  // rides on the list row rather than the document.
  const relatedParty = claims.find((c) => c._id === claim._id)?.relatedParty;
  const [mergeOpen, setMergeOpen] = useState(false);
  const [addingEvidence, setAddingEvidence] = useState(false);
  // Controlled because "Contact" is an action rather than a destination:
  // it opens the creditor's record and leaves the current tab where it is.
  const [activeTab, setActiveTab] = useState("claim");

  // Author identity for the notes feed's edit/delete affordances, and the
  // note count on the Notes tab. Both share the feed's query key.
  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const { data: claimNotes } = useQuery(
    convexQuery(api.jobs.recoveries.notes.listPodClaimNotes, {
      claimId: claim._id,
    }),
  );
  const noteCount = claimNotes?.length ?? 0;

  // The creditor's contact record, resolved server-side by the same resolver
  // the outcome email uses. Opening the record IS the Contact tab — there is
  // nothing worth showing in between.
  const { data: contactLink } = useQuery(
    convexQuery(api.jobs.recoveries.adjudicationEmail.getPodClaimContact, {
      claimId: claim._id,
    }),
  );
  const contactId = contactLink?.contact?.contactId ?? null;

  function openContactRecord() {
    if (contactId) {
      onViewContact(contactId);
      return;
    }
    toast.info(
      `${claim.creditorName} is not on the job's contacts list yet, so there is no record to open. Add them on the Contacts tab.`,
    );
  }

  // Anything not fully accounted for by the creditor's own credits needs
  // a look before admitting.
  const documentNames = new Map(
    claim.sourceDocuments.map((d) => [d.documentId, d.fileName]),
  );
  const unaccountedCount = (detail.reconciledPayments ?? []).filter(
    (p) => p.status !== "credited",
  ).length;

  const setStatus = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.recoveries.publicMutations.setPodClaimStatus,
    ),
  });
  const updateClaim = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.recoveries.publicMutations.updatePodClaim,
    ),
  });
  const deleteClaim = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.recoveries.publicMutations.deletePodClaim,
    ),
  });
  const reopen = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.recoveries.publicMutations.reopenAdjudication,
    ),
  });
  const mergeClaims = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.recoveries.publicMutations.mergePodClaims,
    ),
  });

  // Only non-adjudicated claims on this job can absorb this one.
  const mergeTargets = claims.filter(
    (c) => c._id !== claim._id && c.status !== "adjudicated",
  );

  async function handleMerge(targetId: Id<"podClaims">) {
    try {
      await mergeClaims.mutateAsync({
        sourceClaimId: claim._id,
        targetClaimId: targetId,
      });
      toast.success("Claims merged");
      setMergeOpen(false);
      onMerged(targetId);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to merge claims"));
    }
  }

  async function handleStatus(status: "requested" | "received") {
    try {
      await setStatus.mutateAsync({ claimId: claim._id, status });
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to update status"));
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete the POD claim for ${claim.creditorName}?`)) {
      return;
    }
    try {
      await deleteClaim.mutateAsync({ claimId: claim._id });
      onClose();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to delete claim"));
    }
  }

  return (
    <>
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Header */}
        <div className="border-b border-border px-6 pb-4 pt-6">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <DialogTitle className="text-lg font-bold">
                {claim.creditorName}
              </DialogTitle>
              <div className="flex shrink-0 items-center gap-1">
                <PodClaimExportMenu
                  jobId={claim.jobId}
                  claimIds={[claim._id]}
                />
                {claim.status !== "adjudicated" && mergeTargets.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-1 text-xs text-muted-foreground"
                    onClick={() => setMergeOpen((prev) => !prev)}
                    title="Fold this claim into another — for the same creditor received under a different name"
                  >
                    <GitMerge className="h-3.5 w-3.5" />
                    Merge
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-red-600"
                  onClick={handleDelete}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <DialogDescription className="sr-only">
              Proof of Debt claim detail and adjudication
            </DialogDescription>
          </DialogHeader>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Badge
              color={POD_STATUS_COLORS[claim.status]}
              variant="soft"
              size="sm"
            >
              {POD_STATUS_LABELS[claim.status]}
            </Badge>
            <Badge
              color={POD_CREDITOR_TYPE_COLORS[claim.creditorType]}
              variant="soft"
              size="sm"
            >
              {POD_CREDITOR_TYPE_LABELS[claim.creditorType]}
            </Badge>
            {relatedParty && <RelatedPartyBadge match={relatedParty} />}
            {outcome && (
              <Badge color={outcome.color} variant="soft" size="sm">
                {outcome.label}
              </Badge>
            )}
          </div>

          {/* Lifecycle actions */}
          {claim.status !== "adjudicated" && (
            <div className="mt-3 flex flex-wrap gap-2">
              {claim.status === "not_requested" && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={setStatus.isPending}
                  onClick={() => handleStatus("requested")}
                >
                  Mark POD Requested
                </Button>
              )}
              {claim.status !== "received" && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={setStatus.isPending}
                  onClick={() => handleStatus("received")}
                >
                  Mark POD Received
                </Button>
              )}
            </div>
          )}
          {mergeOpen && (
            <MergePanel
              claim={claim}
              targets={mergeTargets}
              isPending={mergeClaims.isPending}
              onCancel={() => setMergeOpen(false)}
              onConfirm={handleMerge}
            />
          )}
          {(claim.requestedAt || claim.receivedAt) && (
            <p className="mt-2 text-xs text-muted-foreground">
              {claim.requestedAt && (
                <>Requested {fmtTimestamp(claim.requestedAt)}</>
              )}
              {claim.requestedAt && claim.receivedAt && " · "}
              {claim.receivedAt && (
                <>Received {fmtTimestamp(claim.receivedAt)}</>
              )}
            </p>
          )}
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(value) => {
            // "Contact" opens the record instead of switching tabs, so the
            // practitioner never lands on a page of restated detail.
            if (value === "contact") {
              openContactRecord();
              return;
            }
            setActiveTab(value);
          }}
          className="flex min-h-0 flex-1 flex-col"
        >
          <TabsList variant="underline" className="shrink-0 px-6">
            <TabsTrigger variant="underline" value="claim">
              <FileText className="h-3.5 w-3.5" /> Claim &amp; Evidence
            </TabsTrigger>
            <TabsTrigger variant="underline" value="verification">
              <Search className="h-3.5 w-3.5" /> Verification
              {unaccountedCount > 0 && (
                <Badge color="red" variant="soft" size="sm">
                  {unaccountedCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger variant="underline" value="adjudication">
              <Gavel className="h-3.5 w-3.5" /> Adjudication
            </TabsTrigger>
            <TabsTrigger variant="underline" value="notes">
              <StickyNote className="h-3.5 w-3.5" /> Notes
              {noteCount > 0 && (
                <Badge variant="secondary" size="sm">
                  {noteCount}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger
              variant="underline"
              value="contact"
              title={`Open ${claim.creditorName}'s contact record — details, notes, and the correspondence with them`}
            >
              <UserRound className="h-3.5 w-3.5" /> Contact
            </TabsTrigger>
            <TabsTrigger variant="underline" value="history">
              <History className="h-3.5 w-3.5" /> History
            </TabsTrigger>
          </TabsList>

          {/* Claim & Evidence */}
          <TabsContent
            value="claim"
            className="mt-0 min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-4"
          >
            <div className="space-y-6">
              <ClaimDetailsSection
                claim={claim}
                onSave={async (fields) => {
                  try {
                    await updateClaim.mutateAsync({
                      claimId: claim._id,
                      ...fields,
                    });
                  } catch (error) {
                    toast.error(
                      extractErrorMessage(error, "Failed to update claim"),
                    );
                  }
                }}
              />

              <SourceDocumentsSection
                claim={claim}
                onViewDocument={onViewDocument}
                onAddDocument={() => setAddingEvidence(true)}
              />

              <ItemsSection
                claimId={claim._id}
                kind="claimed"
                title="Claim Particulars (Form 535)"
                emptyHint="Add the particulars lines from the creditor's POD form."
                items={claimedItems}
                appointmentDate={detail.appointmentDate}
                paymentMatches={detail.paymentMatches}
                documentNames={documentNames}
                sourceDocuments={claim.sourceDocuments}
                onViewDocument={onViewDocument}
                onAddDocument={() => setAddingEvidence(true)}
                banner={<ClaimTotalBanner detail={detail} />}
              />
              <ItemsSection
                claimId={claim._id}
                kind="substantiated"
                title="Supporting Documentation"
                emptyHint="Add the invoice / statement lines from the creditor's supporting documentation."
                items={substantiatedItems}
                appointmentDate={detail.appointmentDate}
                paymentMatches={detail.paymentMatches}
                documentNames={documentNames}
                sourceDocuments={claim.sourceDocuments}
                onViewDocument={onViewDocument}
                onAddDocument={() => setAddingEvidence(true)}
                banner={<EvidenceBanner detail={detail} />}
              />
            </div>
          </TabsContent>

          {/* Verification */}
          <TabsContent
            value="verification"
            className="mt-0 min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-4"
          >
            <div className="space-y-6">
              <ReconciliationCard detail={detail} />
              <CrossCheckCard detail={detail} onViewDocument={onViewDocument} />
              <ClaimVsPayments detail={detail} />
            </div>
          </TabsContent>

          {/* Adjudication */}
          <TabsContent
            value="adjudication"
            className="mt-0 min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-4"
          >
            {claim.status === "adjudicated" ? (
              <AdjudicatedSummary
                detail={detail}
                onOpenContact={openContactRecord}
                onReopen={async () => {
                  try {
                    await reopen.mutateAsync({ claimId: claim._id });
                  } catch (error) {
                    toast.error(
                      extractErrorMessage(
                        error,
                        "Failed to re-open adjudication",
                      ),
                    );
                  }
                }}
              />
            ) : (
              <AdjudicationForm detail={detail} />
            )}
          </TabsContent>

          {/* Notes */}
          <TabsContent
            value="notes"
            className="mt-0 min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-4"
          >
            <PodClaimNotesFeed
              claimId={claim._id}
              currentUserId={currentUser?._id}
            />
          </TabsContent>

          {/* History */}
          <TabsContent
            value="history"
            className="mt-0 min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-4"
          >
            <ClaimHistory history={claim.changeHistory} />
          </TabsContent>
        </Tabs>
        <AiDisclaimer className="border-t border-border px-6 py-2" />
      </div>

      <ClaimEvidenceDialog
        open={addingEvidence}
        onOpenChange={setAddingEvidence}
        claimId={claim._id}
        jobId={claim.jobId}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Source documents
// ---------------------------------------------------------------------------

/**
 * The documents behind the claim, and the way to add more.
 *
 * Rendered even with nothing attached: an empty claim is exactly when the
 * practitioner needs to be told that dropping the POD form in here saves
 * them typing the particulars out by hand.
 */
function SourceDocumentsSection({
  claim,
  onViewDocument,
  onAddDocument,
}: {
  claim: PodClaimUI;
  onViewDocument: (documentId: Id<"documents">) => void;
  onAddDocument: () => void;
}) {
  const detach = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.recoveries.publicMutations.detachClaimDocument,
    ),
  });

  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-bold">
          Source Documents
          {claim.sourceDocuments.length > 0 &&
            ` (${claim.sourceDocuments.length})`}
        </h3>
        {/* Suppressed while empty — the prompt below is the same action, and
            two buttons for it side by side reads as two different ones. */}
        {claim.sourceDocuments.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 text-xs"
            onClick={onAddDocument}
          >
            <Plus className="h-3.5 w-3.5" />
            Add document
          </Button>
        )}
      </div>

      {claim.sourceDocuments.length === 0 ? (
        <button
          type="button"
          onClick={onAddDocument}
          className="flex w-full items-center gap-3 rounded-lg border border-dashed border-border px-3 py-3 text-left transition-colors hover:bg-muted/50"
        >
          <Upload className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">
            Attach the creditor&apos;s POD form, invoices or statements and
            Morgan will read the claim lines out of them.
          </span>
        </button>
      ) : (
        <>
          <div className="grid gap-2 sm:grid-cols-2">
            {claim.sourceDocuments.map((docRef) => (
              <div
                key={docRef.documentId}
                className="group flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors hover:bg-muted/50"
              >
                <button
                  type="button"
                  onClick={() =>
                    onViewDocument(docRef.documentId as Id<"documents">)
                  }
                  className="flex min-w-0 flex-1 items-center gap-3 text-left"
                >
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                    <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">
                      {docRef.fileName}
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/30 transition-colors group-hover:text-muted-foreground" />
                </button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 shrink-0 p-0 text-muted-foreground opacity-0 transition-opacity hover:text-red-600 group-hover:opacity-100"
                  title="Remove this document and the lines extracted from it"
                  disabled={detach.isPending}
                  onClick={() =>
                    detach
                      .mutateAsync({
                        claimId: claim._id,
                        documentId: docRef.documentId,
                      })
                      .catch((error: unknown) =>
                        toast.error(
                          extractErrorMessage(
                            error,
                            "Failed to remove the document",
                          ),
                        ),
                      )
                  }
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
          <p className="mt-1.5 text-[10px] text-muted-foreground">
            Open a document to check the extracted figures against the source.
          </p>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Claim particulars total
// ---------------------------------------------------------------------------

/**
 * Warns when the particulars do not add up to the headline amount claimed.
 *
 * The commonest cause is the same debt recorded twice — a Form 535 summary
 * row plus the breakdown of that row from a schedule bound into the same
 * pack. Ingestion strips that automatically where the figures pin it down,
 * but extraction is probabilistic and the guard is deliberately
 * conservative, so anything it leaves behind has to be visible rather than
 * quietly feeding the reconciliation.
 */
function ClaimTotalBanner({ detail }: { detail: PodClaimDetailUI }) {
  const { claim, claimedItems } = detail;
  if (claimedItems.length === 0 || claim.claimedTotal <= 0) return null;

  const lineTotal =
    Math.round(claimedItems.reduce((sum, item) => sum + item.amount, 0) * 100) /
    100;
  const difference = Math.round((lineTotal - claim.claimedTotal) * 100) / 100;
  if (Math.abs(difference) < 0.01) return null;

  return (
    <div className="mb-2 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50/60 px-3 py-2.5 dark:border-amber-900 dark:bg-amber-950/30">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700 dark:text-amber-400" />
      <div className="text-[11px] leading-relaxed text-amber-900 dark:text-amber-300">
        <span className="font-semibold">
          These lines do not add up to the amount claimed.
        </span>{" "}
        They total {fmtMoney(lineTotal)} against {fmtMoney(claim.claimedTotal)}{" "}
        on the form —{" "}
        {difference > 0
          ? `${fmtMoney(difference)} too much. Check whether a figure has been recorded twice: a summary row here plus its own breakdown from an attached schedule or statutory demand. Delete the redundant lines.`
          : `${fmtMoney(Math.abs(difference))} short. A particulars row is likely missing.`}{" "}
        The reconciliation uses the amount claimed, not this total.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Evidence provenance
// ---------------------------------------------------------------------------

/**
 * States plainly where the substantiating figures came from.
 *
 * Extraction pulls the claim particulars and the supporting lines out of
 * whatever documents arrive — and a Form 535 pack routinely carries both
 * on facing pages. When that happens the two sections agree perfectly and
 * the reconciliation reports a clean match, which reads as verification
 * when nothing has been verified at all: it is the creditor's own
 * paperwork restated. This says so.
 */
function EvidenceBanner({ detail }: { detail: PodClaimDetailUI }) {
  const evidence = detail.evidence;
  if (!evidence || !evidence.hasSubstantiation) return null;

  if (evidence.sharesSourceWithClaim) {
    return (
      <div className="mb-2 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50/60 px-3 py-2.5 dark:border-amber-900 dark:bg-amber-950/30">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700 dark:text-amber-400" />
        <div className="text-[11px] leading-relaxed text-amber-900 dark:text-amber-300">
          <span className="font-semibold">
            Not independent — same document as the claim.
          </span>{" "}
          These lines were extracted from the creditor&rsquo;s own POD pack, the
          same file the claim particulars came from. They restate the claim
          rather than corroborate it, so a matching total here is not evidence
          the debt is owed. Check the Verification tab for the books and
          records.
        </div>
      </div>
    );
  }

  return (
    <div className="mb-2 flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-[11px] leading-relaxed text-muted-foreground">
      <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
      <span>
        Supplied separately from the claim form — open a line&rsquo;s source
        document to check the figure against the original.
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Claim vs bank, side by side
// ---------------------------------------------------------------------------

type ClaimLineFilter = "all" | "matched" | "unmatched" | "credits";

const CLAIM_LINE_FILTER_LABELS: Record<ClaimLineFilter, string> = {
  all: "All claim lines",
  matched: "Lines up with a payment",
  unmatched: "No matching payment",
  credits: "Credits only",
};

/**
 * Case-folds and drops currency punctuation so "30,000", "$30,000" and
 * "30000" all match the same row.
 */
function normaliseSearch(value: string): string {
  return value.toLowerCase().replace(/[$,]/g, "");
}

/** Builds one searchable string from a row's visible and raw values. */
function searchHaystack(
  ...parts: Array<string | number | undefined | null>
): string {
  return normaliseSearch(
    parts.filter((p) => p !== undefined && p !== null && p !== "").join(" "),
  );
}

/**
 * The claim on the left, the payment it corresponds to on the right, then
 * the payments that correspond to nothing. Lets the practitioner see what
 * was claimed against what actually moved, instead of being told a total.
 *
 * A creditor with a long running account produces dozens of rows across
 * both tables, so one search box filters them together — a practitioner
 * chasing a single invoice number or amount sees it on both sides at once.
 */
function ClaimVsPayments({ detail }: { detail: PodClaimDetailUI }) {
  const data = detail.claimVsPayments;
  const [search, setSearch] = useState("");
  const [lineFilter, setLineFilter] = useState<ClaimLineFilter>("all");

  if (!data) return null;

  const matchedRows = data.rows.filter((r) => r.payment);

  if (data.paidTotal === 0) {
    return (
      <section className="rounded-xl border border-border p-4">
        <h3 className="mb-1 flex items-center gap-1.5 text-sm font-bold">
          <Landmark className="h-4 w-4 text-muted-foreground" />
          Claim vs Bank
        </h3>
        <p className="text-xs text-muted-foreground">
          No payments to this creditor were found in the bank data between the
          earliest claimed invoice and the appointment date — nothing suggests
          any of this claim has already been settled.
        </p>
      </section>
    );
  }

  const query = normaliseSearch(search.trim());

  const visibleRows = data.rows.filter((row) => {
    if (lineFilter === "matched" && !row.payment) return false;
    if (lineFilter === "unmatched" && row.payment) return false;
    if (lineFilter === "credits" && !row.isCredit) return false;
    if (!query) return true;
    // Both sides of the row are searchable — a query that hits the bank
    // description should keep the claim line it sits beside.
    return searchHaystack(
      row.description,
      row.date,
      fmtDate(row.date),
      row.amount,
      fmtMoney(row.amount),
      row.payment?.description,
      row.payment?.date,
      row.payment ? fmtDate(row.payment.date) : undefined,
      row.payment?.amount,
      row.payment ? fmtMoney(row.payment.amount) : undefined,
    ).includes(query);
  });

  const visibleUnmatched = data.unmatchedPayments.filter((payment) => {
    if (!query) return true;
    return searchHaystack(
      payment.description,
      payment.date,
      fmtDate(payment.date),
      payment.amount,
      fmtMoney(payment.amount),
    ).includes(query);
  });
  const visibleUnmatchedTotal = roundTo2(
    visibleUnmatched.reduce((sum, p) => sum + p.amount, 0),
  );
  const filtering = query !== "" || lineFilter !== "all";

  return (
    <section className="rounded-xl border border-border p-4">
      <h3 className="mb-1 flex items-center gap-1.5 text-sm font-bold">
        <Landmark className="h-4 w-4 text-muted-foreground" />
        Claim vs Bank
      </h3>
      <p className="mb-3 text-xs text-muted-foreground">
        Every payment made to this creditor between the earliest claimed invoice
        and the appointment date, set against what they are claiming.
      </p>

      {data.paidTotal > 0 && (
        <div className="mb-3 grid grid-cols-3 gap-3 text-sm">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              Paid in the period
            </p>
            <p className="font-mono font-semibold tabular-nums">
              {fmtMoney(data.paidTotal)}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              Lines up with the claim
            </p>
            <p className="font-mono font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">
              {fmtMoney(data.matchedTotal)}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              Other payments
            </p>
            <p className="font-mono font-semibold tabular-nums text-amber-700 dark:text-amber-400">
              {fmtMoney(data.unmatchedTotal)}
            </p>
          </div>
        </div>
      )}

      {/* One search across both tables — chasing an invoice number or an
          amount should surface it wherever it appears. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search description, date, reference or amount…"
            className="h-9 pl-8 pr-8 text-xs"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <Select
          value={lineFilter}
          onValueChange={(v) => setLineFilter(v as ClaimLineFilter)}
        >
          <SelectTrigger className="h-9 w-[200px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(
              Object.entries(CLAIM_LINE_FILTER_LABELS) as Array<
                [ClaimLineFilter, string]
              >
            ).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-hidden rounded-lg border border-border">
        <table className="w-full table-fixed text-sm">
          <colgroup>
            <col className="w-1/2" />
            <col className="w-1/2" />
          </colgroup>
          <thead>
            <tr className="bg-muted/40">
              <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Claimed
              </th>
              <th className="border-l border-border px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Matching bank payment
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.length === 0 && (
              <tr className="border-t border-border">
                <td
                  colSpan={2}
                  className="px-3 py-6 text-center text-xs text-muted-foreground"
                >
                  No claim lines match
                  {query ? ` “${search.trim()}”` : " this filter"}.
                </td>
              </tr>
            )}
            {visibleRows.map((row) => (
              <tr key={row.lineId} className="border-t border-border align-top">
                <td className="px-3 py-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs">{row.description}</p>
                      <p className="mt-0.5 text-[10px] text-muted-foreground">
                        {fmtDate(row.date)}
                      </p>
                    </div>
                    <span
                      className={cn(
                        "shrink-0 font-mono text-xs font-semibold tabular-nums",
                        row.isCredit &&
                          "text-emerald-700 dark:text-emerald-400",
                      )}
                    >
                      {fmtMoney(row.amount)}
                    </span>
                  </div>
                </td>
                <td className="border-l border-border px-3 py-2">
                  {row.payment ? (
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-xs">
                          {row.payment.description}
                        </p>
                        <p className="mt-0.5 text-[10px]">
                          <span className="text-muted-foreground">
                            {fmtDate(row.payment.date)}
                          </span>{" "}
                          {row.match === "invoice_settled" ? (
                            <span className="font-medium text-red-700 dark:text-red-400">
                              · this charge may already be paid
                            </span>
                          ) : (
                            <span className="font-medium text-emerald-700 dark:text-emerald-400">
                              · credited by the creditor
                            </span>
                          )}
                        </p>
                      </div>
                      <span className="shrink-0 font-mono text-xs font-semibold tabular-nums">
                        {fmtMoney(row.payment.amount)}
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      No matching payment
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-1.5 text-[10px] text-muted-foreground">
        {matchedRows.length} of {data.rows.length} claim line
        {data.rows.length !== 1 ? "s" : ""} line up with a payment.
        {filtering && (
          <>
            {" "}
            Showing {visibleRows.length} of {data.rows.length}.
          </>
        )}
      </p>

      {data.unmatchedPayments.length > 0 && (
        <div className="mt-4">
          <div className="mb-1.5 flex flex-wrap items-center gap-2">
            <h4 className="text-xs font-semibold">
              Other payments to this creditor
            </h4>
            <Badge color="amber" variant="soft" size="sm">
              {query
                ? `${visibleUnmatched.length} of ${data.unmatchedPayments.length}`
                : data.unmatchedPayments.length}
            </Badge>
            <span className="font-mono text-xs font-semibold tabular-nums">
              {fmtMoney(query ? visibleUnmatchedTotal : data.unmatchedTotal)}
            </span>
            {query && (
              <span className="text-[10px] text-muted-foreground">
                matching “{search.trim()}”
              </span>
            )}
          </div>
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <tbody>
                {visibleUnmatched.length === 0 && (
                  <tr>
                    <td className="px-3 py-6 text-center text-xs text-muted-foreground">
                      No other payments match “{search.trim()}”.
                    </td>
                  </tr>
                )}
                {visibleUnmatched.map((payment, index) => (
                  <tr
                    key={`${payment.date}-${index}`}
                    className="border-b border-border last:border-b-0"
                  >
                    <td className="w-[86px] px-3 py-2 align-top text-xs text-muted-foreground">
                      {fmtDate(payment.date)}
                    </td>
                    <td className="px-2 py-2 align-top text-xs">
                      {payment.description}
                    </td>
                    <td className="w-[110px] px-3 py-2 text-right align-top">
                      <span className="font-mono text-xs font-semibold tabular-nums">
                        {fmtMoney(payment.amount)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-1.5 text-[10px] leading-relaxed text-muted-foreground">
            These do not line up with anything on the claim. They may be
            unrelated dealings, earlier invoices already settled, or amounts the
            creditor has not credited — worth understanding before admitting,
            and worth checking against voidable transactions.
          </p>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

/**
 * The claim's audit trail — every change, including automated ones, newest
 * first. Adjudication is a determination that a creditor can appeal, so the
 * record of how the figures got to where they are matters.
 *
 * Renders through the shared `ChangeHistory` component so the trail looks and
 * behaves like every other record panel; the POD-specific part is the
 * formatter / label maps for the before-and-after diff.
 */
function ClaimHistory({
  history,
}: {
  history: PodClaimDetailUI["claim"]["changeHistory"];
}) {
  // Resolve practitioner identities so entries show a full name and avatar
  // rather than the free-text `actor` string captured at write time.
  const historyUserIds = useMemo(() => {
    const seen = new Set<Id<"users">>();
    for (const entry of history) {
      if (entry.userId) seen.add(entry.userId);
    }
    return Array.from(seen);
  }, [history]);

  const { data: historyUsers } = useQuery({
    ...convexQuery(api.users.queries.listUsersByIds, { ids: historyUserIds }),
    enabled: historyUserIds.length > 0,
  });

  const usersById = useMemo(() => {
    const map = new Map<Id<"users">, ResolvedHistoryUser>();
    for (const u of historyUsers ?? []) map.set(u._id, u);
    return map;
  }, [historyUsers]);

  if (history.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No changes recorded yet.
      </p>
    );
  }

  return (
    <ChangeHistory
      history={history}
      defaultOpen
      maxEntries={history.length}
      usersById={usersById}
      formatters={POD_CLAIM_FIELD_FORMATTERS}
      fieldLabels={POD_CLAIM_FIELD_LABELS}
    />
  );
}

// ---------------------------------------------------------------------------
// Merge
// ---------------------------------------------------------------------------

/**
 * Inline merge control. Rendered in the panel body rather than a dialog:
 * this panel is already a Sheet, and a nested overlay renders behind it.
 */
function MergePanel({
  claim,
  targets,
  isPending,
  onCancel,
  onConfirm,
}: {
  claim: PodClaimDetailUI["claim"];
  targets: PodClaimUI[];
  isPending: boolean;
  onCancel: () => void;
  onConfirm: (targetId: Id<"podClaims">) => void;
}) {
  const [targetId, setTargetId] = useState<string>("");
  const target = targets.find((t) => t._id === targetId);

  return (
    <div className="mt-3 rounded-lg border border-border bg-muted/40 p-3">
      <p className="text-xs font-semibold">Merge into another claim</p>
      <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
        Use this when the same creditor came through under two names. Every line
        item and source document on <strong>{claim.creditorName}</strong> moves
        to the claim you pick, and this one is deleted. Details already set on
        the other claim are kept.
      </p>

      <div className="mt-2.5">
        <label className={LABEL_CLASS}>Merge into</label>
        <Select value={targetId} onValueChange={setTargetId}>
          <SelectTrigger className="text-sm">
            <SelectValue placeholder="Select a claim…" />
          </SelectTrigger>
          <SelectContent>
            {targets.map((t) => (
              <SelectItem key={t._id} value={t._id}>
                {t.creditorName}
                {t.claimedTotal > 0 ? ` — ${fmtMoney(t.claimedTotal)}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {target && (
        <p className="mt-2 flex items-start gap-1.5 text-[11px] leading-relaxed text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          <span>
            &ldquo;{claim.creditorName}&rdquo; will be deleted and its evidence
            moved into &ldquo;{target.creditorName}&rdquo;. This cannot be
            undone.
          </span>
        </p>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!target || isPending}
          onClick={() => target && onConfirm(target._id)}
        >
          {isPending ? "Merging…" : "Merge"}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cross-check against the books and records
// ---------------------------------------------------------------------------

/**
 * Notes held against a creditors-table row. The summarisation agents write
 * markdown, so rendering the raw string shows the asterisks and dashes, and
 * two clamped lines cut the note off mid-sentence. Mirrors the notes cell
 * on the Creditors table so the same note reads the same way in both places.
 */
function LedgerRowNotes({ notes }: { notes: string }) {
  const [expanded, setExpanded] = useState(false);
  // Agents emit inline `• item • item` runs; turn them into real bullets.
  const normalised = notes.replace(/\s*•\s*/g, "\n- ").trim();
  if (!normalised) return null;

  return (
    <div className="mt-1 flex min-w-0 flex-col gap-1">
      <div
        className={cn(
          "prose-xs prose max-w-none dark:prose-invert",
          !expanded && "line-clamp-2",
          "[&_p]:m-0 [&_p]:text-[10px] [&_p]:leading-relaxed [&_p]:text-muted-foreground",
          "[&_strong]:font-semibold [&_strong]:text-foreground",
          "[&_ul]:m-0 [&_ul]:list-disc [&_ul]:pl-3.5",
          "[&_li]:text-[10px] [&_li]:leading-relaxed [&_li]:text-muted-foreground",
        )}
      >
        <ReactMarkdown>{normalised}</ReactMarkdown>
      </div>
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex items-center gap-1 self-start text-[10px] font-medium uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground"
      >
        {expanded ? (
          <>
            <ChevronsDownUp className="h-3 w-3" />
            Show less
          </>
        ) : (
          <>
            <ChevronsUpDown className="h-3 w-3" />
            Show more
          </>
        )}
      </button>
    </div>
  );
}

/**
 * The POD and its supporting documents both come from the creditor, so
 * reconciling one against the other only proves internal consistency. This
 * card is the independent leg: what the books and records say is owed,
 * and what the bank shows was actually paid.
 */
function CrossCheckCard({
  detail,
  onViewDocument,
}: {
  detail: PodClaimDetailUI;
  onViewDocument: (documentId: Id<"documents">) => void;
}) {
  const cross = detail.crossCheck;
  if (!cross) return null;

  const hasLedger = cross.ledgerAmount !== null;
  if (!hasLedger && cross.ledgerRows.length === 0) {
    return (
      <section className="rounded-xl border border-border p-4">
        <h3 className="mb-1 flex items-center gap-1.5 text-sm font-bold">
          <Landmark className="h-4 w-4 text-muted-foreground" />
          Books &amp; Records Check
        </h3>
        <p className="text-xs text-muted-foreground">
          This creditor does not appear on the creditors table, so the books and
          records offer no independent view of what is owed. Payments to them
          are reconciled below.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-border p-4">
      <h3 className="mb-1 flex items-center gap-1.5 text-sm font-bold">
        <Landmark className="h-4 w-4 text-muted-foreground" />
        Books &amp; Records Check
      </h3>
      <p className="mb-3 text-xs text-muted-foreground">
        What the creditors ledger records as owing to this creditor, independent
        of anything they have told us.
      </p>

      {hasLedger && (
        <div className="mb-3 grid grid-cols-3 gap-3 text-sm">
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              They claim
            </p>
            <p className="font-mono font-semibold tabular-nums">
              {fmtMoney(detail.claim.claimedTotal)}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              Our books
            </p>
            <p className="font-mono font-semibold tabular-nums">
              {fmtMoney(cross.ledgerAmount ?? 0)}
            </p>
            <p className="text-[10px] text-muted-foreground">
              {cross.ledgerRowCount} independent creditor row
              {cross.ledgerRowCount !== 1 ? "s" : ""}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              Difference
            </p>
            <p
              className={cn(
                "font-mono font-semibold tabular-nums",
                Math.abs(cross.ledgerVariance ?? 0) < 0.005
                  ? "text-emerald-700 dark:text-emerald-400"
                  : "text-amber-700 dark:text-amber-400",
              )}
            >
              {Math.abs(cross.ledgerVariance ?? 0) < 0.005
                ? "Nil"
                : fmtMoney(cross.ledgerVariance ?? 0)}
            </p>
          </div>
        </div>
      )}

      {/* The creditors-table rows behind "our books" */}
      {cross.ledgerRows.length > 0 && (
        <div className="mb-3">
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Creditors table ({cross.ledgerRows.length})
          </p>
          <div className="overflow-hidden rounded-lg border border-border">
            {cross.ledgerRows.map((row) => (
              <div
                key={row.id}
                className={cn(
                  "border-b border-border px-3 py-2 last:border-b-0",
                  row.derivedFromPod && "bg-muted/40",
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium">
                      {row.creditorName}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-1">
                      <Badge
                        color={row.derivedFromPod ? "amber" : "blue"}
                        variant="soft"
                        size="sm"
                      >
                        {LIABILITY_SOURCE_LABELS[row.source] ?? row.source}
                      </Badge>
                      <Badge color="gray" variant="soft" size="sm">
                        {LIABILITY_NATURE_LABELS[row.natureOfDebt] ??
                          row.natureOfDebt}
                      </Badge>
                      {row.derivedFromPod && (
                        <span className="text-[10px] font-medium text-amber-700 dark:text-amber-400">
                          excluded — derived from a POD
                        </span>
                      )}
                    </div>
                    {row.notes && <LedgerRowNotes notes={row.notes} />}
                    {row.sourceDocuments.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {row.sourceDocuments.map((docRef) => (
                          <button
                            key={docRef.documentId}
                            type="button"
                            onClick={() =>
                              onViewDocument(
                                docRef.documentId as Id<"documents">,
                              )
                            }
                            className="inline-flex max-w-[200px] items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[10px] transition-colors hover:bg-muted"
                            title={docRef.fileName}
                          >
                            <FileText className="h-2.5 w-2.5 shrink-0 text-muted-foreground" />
                            <span className="truncate">{docRef.fileName}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* An excluded row's figure is struck through: it is on the
                      creditors table, but it counts for nothing here, and
                      reading it as corroboration of the claim it came from is
                      the exact mistake this card exists to prevent. Amber, not
                      red — red means a shortfall elsewhere in this panel. */}
                  <span
                    className={cn(
                      "shrink-0 font-mono text-xs font-semibold tabular-nums",
                      row.derivedFromPod &&
                        "text-amber-700 line-through decoration-amber-700/60 dark:text-amber-400 dark:decoration-amber-400/60",
                    )}
                    title={
                      row.derivedFromPod
                        ? "Not counted — this row was written from a proof of debt, so it is not independent of the claim"
                        : undefined
                    }
                  >
                    {fmtMoney(row.amount)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        {cross.podDerivedRowCount > 0 && (
          <ReconNote tone="warn">
            {cross.podDerivedRowCount} creditors-table row
            {cross.podDerivedRowCount !== 1 ? "s" : ""} totalling{" "}
            {fmtMoney(cross.podDerivedAmount)}{" "}
            {cross.podDerivedRowCount !== 1 ? "were" : "was"} written from a
            proof of debt, so{" "}
            {cross.podDerivedRowCount !== 1 ? "they have" : "it has"} been
            excluded as{" "}
            {cross.podDerivedRowCount !== 1 ? "they have" : "it has"} not been
            verified independent of the POD.
          </ReconNote>
        )}
        {hasLedger && Math.abs(cross.ledgerVariance ?? 0) < 0.005 && (
          <ReconNote tone="ok">
            The creditors ledger agrees with the amount claimed.
          </ReconNote>
        )}
        {hasLedger && (cross.ledgerVariance ?? 0) > 0.005 && (
          <ReconNote tone="warn">
            They claim {fmtMoney(cross.ledgerVariance ?? 0)} more than the books
            record as owing.
          </ReconNote>
        )}
        {hasLedger && (cross.ledgerVariance ?? 0) < -0.005 && (
          <ReconNote tone="warn">
            The books record {fmtMoney(Math.abs(cross.ledgerVariance ?? 0))}{" "}
            more owing than they have claimed — the claim may be understated.
          </ReconNote>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Claim details (editable)
// ---------------------------------------------------------------------------

function ClaimDetailsSection({
  claim,
  onSave,
}: {
  claim: PodClaimDetailUI["claim"];
  onSave: (fields: {
    claimedTotal?: number;
    podDate?: string;
    creditorReference?: string;
    securityHeldValue?: number;
    securityHeldDescription?: string;
    creditorType?: PodCreditorType;
  }) => Promise<void>;
}) {
  const [claimedTotal, setClaimedTotal] = useState(
    claim.claimedTotal > 0 ? String(claim.claimedTotal) : "",
  );
  const [podDate, setPodDate] = useState(claim.podDate ?? "");
  const [reference, setReference] = useState(claim.creditorReference ?? "");
  const [security, setSecurity] = useState(
    claim.securityHeldValue !== undefined
      ? String(claim.securityHeldValue)
      : "",
  );
  const [securityNote, setSecurityNote] = useState(
    claim.securityHeldDescription ?? "",
  );

  // Re-sync local state when another client updates the claim.
  useEffect(() => {
    setClaimedTotal(claim.claimedTotal > 0 ? String(claim.claimedTotal) : "");
    setPodDate(claim.podDate ?? "");
    setReference(claim.creditorReference ?? "");
    setSecurity(
      claim.securityHeldValue !== undefined
        ? String(claim.securityHeldValue)
        : "",
    );
    setSecurityNote(claim.securityHeldDescription ?? "");
  }, [
    claim.claimedTotal,
    claim.podDate,
    claim.creditorReference,
    claim.securityHeldValue,
    claim.securityHeldDescription,
  ]);

  const parsedTotal = claimedTotal ? parseFloat(claimedTotal) : 0;
  const parsedSecurity = security ? parseFloat(security) : undefined;
  const securityValue =
    parsedSecurity !== undefined && Number.isFinite(parsedSecurity)
      ? parsedSecurity
      : undefined;
  // Only a secured creditor nets security off their proof, so the fields are
  // noise on every other claim. Still shown when a figure is already recorded,
  // so a value stated on the form can never sit on the claim unreachable.
  const showSecurity =
    claim.creditorType === "secured" ||
    claim.securityHeldValue !== undefined ||
    !!claim.securityHeldDescription;
  const dirty =
    (Number.isFinite(parsedTotal) ? parsedTotal : 0) !== claim.claimedTotal ||
    (podDate || undefined) !== claim.podDate ||
    (reference || undefined) !== claim.creditorReference ||
    securityValue !== claim.securityHeldValue ||
    (securityNote || undefined) !== claim.securityHeldDescription;

  return (
    <section>
      <h3 className="mb-3 text-sm font-bold">Claim Details</h3>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className={LABEL_CLASS}>Amount Claimed</label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={claimedTotal}
            onChange={(e) => setClaimedTotal(e.target.value)}
            placeholder="0.00"
          />
        </div>
        <div>
          <label className={LABEL_CLASS}>POD Date</label>
          <DatePicker value={podDate} onChange={setPodDate} />
        </div>
        <div>
          <label className={LABEL_CLASS}>Creditor Reference</label>
          <Input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="Optional"
          />
        </div>
        {showSecurity && (
          <>
            <div>
              <label className={LABEL_CLASS}>Security Held (Value)</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={security}
                onChange={(e) => setSecurity(e.target.value)}
                placeholder="0.00"
              />
              <p className="mt-1 text-[10px] text-muted-foreground">
                The creditor&rsquo;s own estimate on the form. Deducted from the
                claim to give what ranks unsecured.
              </p>
            </div>
            <div className="col-span-2">
              <label className={LABEL_CLASS}>Security Particulars</label>
              <Input
                value={securityNote}
                onChange={(e) => setSecurityNote(e.target.value)}
                placeholder="e.g. Registered mortgage over 12 Smith St, Parramatta"
              />
            </div>
          </>
        )}
      </div>
      {dirty && (
        <div className="mt-2 flex justify-end">
          <Button
            size="sm"
            onClick={() =>
              onSave({
                claimedTotal: Number.isFinite(parsedTotal) ? parsedTotal : 0,
                podDate: podDate || undefined,
                creditorReference: reference || undefined,
                securityHeldValue: securityValue,
                securityHeldDescription: securityNote || undefined,
                // Security is only netted off a claim marked secured, so
                // stating a value has to mark it — otherwise the figure sits
                // on the claim doing nothing and the schedule reads wrong.
                creditorType:
                  securityValue !== undefined &&
                  claim.creditorType === "unsecured"
                    ? "secured"
                    : undefined,
              })
            }
          >
            Save Details
          </Button>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Reconciliation card
// ---------------------------------------------------------------------------

/** Tooltip wording for the estimated value, by where the figure came from. */
const ESTIMATE_HINTS: Record<"books" | "pod" | "mixed", string> = {
  books:
    "Est value is what the creditor is estimated to be owed based on the books and records.",
  pod: "Est value is what the creditor is estimated to be owed based on the books and records — but the only creditors-table row for this creditor was itself written from this proof of debt, so it restates the claim rather than corroborating it.",
  mixed:
    "Est value is what the creditor is estimated to be owed based on the books and records — but part of it comes from a creditors-table row written from this proof of debt, so that part restates the claim rather than corroborating it.",
};

function ReconciliationCard({ detail }: { detail: PodClaimDetailUI }) {
  const { reconciliation: r, appointmentDate } = detail;
  const hasClaim = r.claimedTotal > 0;
  const hasSubstantiation = detail.substantiatedItems.length > 0;
  const estimated = detail.claim.estimatedValue;
  // A figure the creditors table only holds because this creditor lodged a POD
  // is the claim read back to us. It still belongs on the card — it is what the
  // books say — but it corroborates nothing, so it is never shown plain.
  const estimateSource = detail.claim.estimatedValueSource;
  const estimateFromPod =
    estimateSource === "pod" || estimateSource === "mixed";

  return (
    <section className="rounded-xl border border-border bg-muted/30 p-4">
      <h3 className="mb-3 text-sm font-bold">Reconciliation</h3>
      <div className="grid grid-cols-4 gap-3 text-sm">
        <div>
          <p className="flex items-center gap-1 text-xs uppercase tracking-wider text-muted-foreground">
            Total Est. Value
            <InfoHint text={ESTIMATE_HINTS[estimateSource ?? "books"]} />
          </p>
          <p
            className={cn(
              "font-mono font-semibold tabular-nums",
              estimateFromPod
                ? "text-amber-700 dark:text-amber-400"
                : "text-muted-foreground",
            )}
          >
            {estimated !== undefined ? fmtMoney(estimated) : "—"}
          </p>
          {estimateFromPod && (
            <p className="mt-0.5 text-[10px] font-medium leading-tight text-amber-700 dark:text-amber-400">
              {estimateSource === "pod"
                ? "from this POD — not independent"
                : "partly from this POD"}
            </p>
          )}
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Claimed
          </p>
          <p className="font-mono font-semibold tabular-nums">
            {fmtMoney(r.claimedTotal)}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Substantiated
          </p>
          <p className="font-mono font-semibold tabular-nums">
            {fmtMoney(r.substantiatedTotal)}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Variance
          </p>
          <p
            className={cn(
              "font-mono font-semibold tabular-nums",
              r.matched
                ? "text-emerald-700 dark:text-emerald-400"
                : "text-red-700 dark:text-red-400",
            )}
          >
            {r.matched ? "Nil" : fmtMoney(r.variance)}
          </p>
        </div>
      </div>

      <div className="mt-3 space-y-1.5">
        {!hasClaim && (
          <ReconNote tone="muted">
            Enter the amount claimed on the POD form to reconcile.
          </ReconNote>
        )}
        {hasClaim && !hasSubstantiation && (
          <ReconNote tone="warn">
            No supporting documentation or evidence of claim.
          </ReconNote>
        )}
        {hasClaim && hasSubstantiation && r.matched && (
          <ReconNote tone="ok">
            Supporting documents reconcile to the amount claimed. Ready to admit
            in full.
          </ReconNote>
        )}
        {hasClaim && hasSubstantiation && !r.matched && r.variance > 0 && (
          <ReconNote tone="warn">
            Supporting documents fall {fmtMoney(r.variance)} short of the claim
            — suggest admitting {fmtMoney(r.suggestedAdmit)} and rejecting{" "}
            {fmtMoney(r.suggestedReject)} as unproven.
          </ReconNote>
        )}
        {hasClaim && hasSubstantiation && !r.matched && r.variance < 0 && (
          <ReconNote tone="warn">
            Supporting documents exceed the claim by{" "}
            {fmtMoney(Math.abs(r.variance))} — the creditor may lodge an amended
            POD; admission is capped at the amount claimed (
            {fmtMoney(r.suggestedAdmit)}).
          </ReconNote>
        )}
        {r.postAppointmentItemCount > 0 && (
          <ReconNote tone="warn">
            {r.postAppointmentItemCount} supporting line
            {r.postAppointmentItemCount !== 1 ? "s" : ""} dated after the
            appointment date ({fmtDate(appointmentDate)}) totalling{" "}
            {fmtMoney(r.postAppointmentAmount)} — post-appointment debts are
            excluded from the suggested admission.
          </ReconNote>
        )}
      </div>
    </section>
  );
}

/** Small info icon with an explanatory tooltip (keeps the card uncluttered). */
function InfoHint({ text }: { text: string }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="text-muted-foreground/50 transition-colors hover:text-muted-foreground"
            aria-label="More info"
          >
            <Info className="h-3 w-3" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs font-normal leading-relaxed">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function ReconNote({
  tone,
  children,
}: {
  tone: "ok" | "warn" | "muted";
  children: React.ReactNode;
}) {
  return (
    <p
      className={cn(
        "flex items-start gap-1.5 text-xs leading-relaxed",
        tone === "ok" && "text-emerald-700 dark:text-emerald-400",
        tone === "warn" && "text-amber-700 dark:text-amber-400",
        tone === "muted" && "text-muted-foreground",
      )}
    >
      {tone === "ok" ? (
        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      ) : tone === "warn" ? (
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      ) : null}
      <span>{children}</span>
    </p>
  );
}

// ---------------------------------------------------------------------------
// Line items
// ---------------------------------------------------------------------------

/**
 * Where a line's figures came from.
 *
 * An extracted line and a typed line citing the same invoice look alike on
 * screen but mean different things when the adjudication is challenged,
 * so the wording distinguishes them.
 */
function ItemProvenance({
  item,
  documentNames,
  onViewDocument,
}: {
  item: PodClaimItemUI;
  documentNames: Map<string, string>;
  onViewDocument: (documentId: Id<"documents">) => void;
}) {
  const documentId = item.sourceDocumentId ?? item.attachedDocumentId;
  if (!documentId) {
    return (
      <span className="text-[10px] italic text-muted-foreground">
        Entered manually — no source document
      </span>
    );
  }

  const fileName = documentNames.get(documentId) ?? "Source document";
  const extracted = !!item.sourceDocumentId;

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onViewDocument(documentId as Id<"documents">);
      }}
      className="inline-flex max-w-[280px] items-center gap-1 text-[10px] text-muted-foreground underline decoration-dotted underline-offset-2 transition-colors hover:text-foreground"
      title={
        extracted
          ? `Extracted from ${fileName}`
          : `Entered by hand, citing ${fileName}`
      }
    >
      <FileText className="h-2.5 w-2.5 shrink-0" />
      <span className="truncate">
        {extracted ? fileName : `Cites ${fileName}`}
      </span>
    </button>
  );
}

function ItemsSection({
  claimId,
  kind,
  title,
  emptyHint,
  items,
  appointmentDate,
  paymentMatches,
  documentNames,
  sourceDocuments,
  onViewDocument,
  onAddDocument,
  banner,
}: {
  claimId: Id<"podClaims">;
  kind: PodClaimItemKind;
  title: string;
  emptyHint: string;
  items: PodClaimItemUI[];
  appointmentDate: string | null;
  /** Line id → bank payment that appears to have settled it. */
  paymentMatches?: Record<string, PodCrossCheckPaymentUI>;
  /** documentId → file name, for showing where each line came from. */
  documentNames: Map<string, string>;
  /** Documents already on the claim, offered as citations on a typed line. */
  sourceDocuments: Array<{ documentId: string; fileName: string }>;
  onViewDocument: (documentId: Id<"documents">) => void;
  /** Opens the shared add-evidence dialog. */
  onAddDocument: () => void;
  /** Optional provenance warning rendered above the lines. */
  banner?: React.ReactNode;
}) {
  const [adding, setAdding] = useState(false);
  const [date, setDate] = useState("");
  const [reference, setReference] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [attachedDocumentId, setAttachedDocumentId] = useState("");

  const addItem = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.recoveries.publicMutations.addPodClaimItem,
    ),
  });
  const deleteItem = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.recoveries.publicMutations.deletePodClaimItem,
    ),
  });

  const total = items.reduce((sum, item) => sum + item.amount, 0);

  function isPostAppointment(itemDate?: string): boolean {
    if (!itemDate || !appointmentDate) return false;
    const item = Date.parse(itemDate);
    const appointment = Date.parse(appointmentDate);
    if (Number.isNaN(item) || Number.isNaN(appointment)) return false;
    return item > appointment;
  }

  async function handleAdd() {
    const parsed = parseFloat(amount);
    if (!description.trim() || !Number.isFinite(parsed)) return;
    try {
      await addItem.mutateAsync({
        claimId,
        kind,
        date: date || undefined,
        reference: reference.trim() || undefined,
        description: description.trim(),
        amount: parsed,
        attachedDocumentId: attachedDocumentId || undefined,
      });
      setDate("");
      setReference("");
      setDescription("");
      setAmount("");
      setAttachedDocumentId("");
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to add line item"));
    }
  }

  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-bold">{title}</h3>
        <div className="flex items-center gap-3">
          {items.length > 0 && (
            <span className="font-mono text-xs font-semibold tabular-nums text-muted-foreground">
              {fmtMoney(total)}
            </span>
          )}
          {/* Uploading lives in Source Documents above: extraction decides
              which of these sections each line belongs to, so an upload
              button here would not mean anything different. */}
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 text-xs"
            onClick={() => setAdding((prev) => !prev)}
          >
            <Plus className="h-3.5 w-3.5" />
            Add line
          </Button>
        </div>
      </div>

      {banner}

      {items.length === 0 && !adding ? (
        <div className="rounded-lg border border-dashed border-border px-3 py-3">
          <p className="text-xs text-muted-foreground">{emptyHint}</p>
          <p className="mt-1 text-[10px] text-muted-foreground">
            Attach the document under Source Documents and Morgan reads these
            lines out of it, or enter them yourself and cite the file they came
            from.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <tbody>
              {items.map((item) => {
                const flagged = isPostAppointment(item.date);
                const paid = paymentMatches?.[item._id];
                return (
                  <tr
                    key={item._id}
                    className="group border-b border-border last:border-b-0"
                  >
                    {/* `leading-5` matches the description cell's first line so
                        the flag lines up with the source-document row beside it. */}
                    <td className="w-[132px] px-3 py-2 align-top text-xs leading-5 text-muted-foreground">
                      {fmtDate(item.date)}
                      {/* Sits with the date because that is what triggers it. */}
                      {flagged && (
                        <span
                          className="mt-0.5 flex items-center gap-0.5 whitespace-nowrap text-[10px] font-medium text-amber-700 dark:text-amber-400"
                          title="Dated after the appointment date — post-appointment debts are excluded"
                        >
                          <AlertTriangle className="h-3 w-3 shrink-0" />
                          Post-appointment
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 align-top">
                      <span className="text-xs">{item.description}</span>
                      {item.reference && (
                        <span className="ml-1.5 text-[10px] text-muted-foreground">
                          #{item.reference}
                        </span>
                      )}
                      {/* Where this figure actually came from — extracted
                          from a document, typed against one, or neither. */}
                      <span className="mt-0.5 block">
                        <ItemProvenance
                          item={item}
                          documentNames={documentNames}
                          onViewDocument={onViewDocument}
                        />
                      </span>
                      {paid && (
                        <span
                          className="ml-1.5 inline-flex items-center gap-0.5 text-[10px] font-medium text-red-700 dark:text-red-400"
                          title={`A bank payment of ${fmtMoney(paid.amount)} to this creditor on ${fmtDate(paid.date)} matches this line — verify it has not already been settled before admitting it`}
                        >
                          <AlertTriangle className="h-3 w-3" />
                          Possibly paid {fmtDate(paid.date)}
                        </span>
                      )}
                    </td>
                    <td className="w-[110px] px-3 py-2 text-right align-top">
                      <span
                        className={cn(
                          "font-mono text-xs font-semibold tabular-nums",
                          item.amount < 0 &&
                            "text-emerald-700 dark:text-emerald-400",
                        )}
                      >
                        {fmtMoney(item.amount)}
                      </span>
                    </td>
                    <td className="w-[36px] px-1 py-1.5 text-right align-top">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground opacity-0 transition-opacity hover:text-red-600 group-hover:opacity-100"
                        onClick={() =>
                          deleteItem
                            .mutateAsync({ itemId: item._id })
                            .catch((error: unknown) =>
                              toast.error(
                                extractErrorMessage(
                                  error,
                                  "Failed to delete line item",
                                ),
                              ),
                            )
                        }
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {adding && (
        <div className="mt-2 flex flex-wrap items-end gap-2 rounded-lg border border-border bg-muted/30 p-3">
          <div className="w-[150px]">
            <label className={LABEL_CLASS}>Date</label>
            <DatePicker value={date} onChange={setDate} />
          </div>
          <div className="w-[90px]">
            <label className={LABEL_CLASS}>Ref</label>
            <Input
              className="h-9"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Inv #"
            />
          </div>
          <div className="min-w-[140px] flex-1">
            <label className={LABEL_CLASS}>Description</label>
            <Input
              className="h-9"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Renewal premium"
            />
          </div>
          <div className="w-[110px]">
            <label className={LABEL_CLASS}>Amount</label>
            <Input
              className="h-9"
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
            />
          </div>
          <div className="min-w-[180px] flex-1">
            <label className={LABEL_CLASS}>Source file</label>
            <Select
              value={attachedDocumentId || NO_SOURCE_FILE}
              onValueChange={(value) => {
                if (value === ADD_SOURCE_FILE) {
                  onAddDocument();
                  return;
                }
                setAttachedDocumentId(value === NO_SOURCE_FILE ? "" : value);
              }}
            >
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_SOURCE_FILE}>No file</SelectItem>
                {sourceDocuments.map((docRef) => (
                  <SelectItem key={docRef.documentId} value={docRef.documentId}>
                    {docRef.fileName}
                  </SelectItem>
                ))}
                <SelectItem value={ADD_SOURCE_FILE}>
                  Attach a file to this claim…
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            size="sm"
            disabled={
              addItem.isPending ||
              !description.trim() ||
              !Number.isFinite(parseFloat(amount))
            }
            onClick={handleAdd}
          >
            Add
          </Button>
        </div>
      )}
      {adding && (
        <p className="mt-1 text-[10px] text-muted-foreground">
          {kind === "substantiated"
            ? "Enter payments / credits as negative amounts. Cite the file the figure came from so the adjudication can be checked back to source."
            : "Cite the file the figure came from so the adjudication can be checked back to source."}
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Adjudication form
// ---------------------------------------------------------------------------

function AdjudicationForm({ detail }: { detail: PodClaimDetailUI }) {
  const { claim, reconciliation: r } = detail;

  // Mirrors the guard in `draftAdjudication`: the model drafts from the
  // extracted particulars, so with nothing extracted there is nothing for it
  // to work from. Checked here so the button says so up front rather than
  // the action failing after the click.
  const nothingToDraftFrom =
    detail.claimedItems.length === 0 &&
    detail.substantiatedItems.length === 0 &&
    claim.claimedTotal === 0;

  // Seeded from what has already been decided, then from the reconciliation
  // — never a bare "admit", which a practitioner rejecting a claim in full
  // would have no reason to touch, leaving "Admit" recorded against a
  // rejection.
  const [recommendation, setRecommendation] = useState<PodRecommendation>(
    () => claim.recommendation ?? (r.suggestedAdmit > 0 ? "admit" : "reject"),
  );
  const [admitted, setAdmitted] = useState(
    String(claim.admittedAmount ?? r.suggestedAdmit),
  );
  const [rejected, setRejected] = useState(
    String(claim.rejectedAmount ?? r.suggestedReject),
  );
  const [grounds, setGrounds] = useState(claim.groundsForRejection ?? "");
  const [docTypes, setDocTypes] = useState<Set<PodSupportingDocType>>(
    new Set(claim.supportingDocTypes),
  );
  const [docOther, setDocOther] = useState(claim.supportingDocOther ?? "");
  const [podExecuted, setPodExecuted] = useState(claim.podExecuted ?? false);
  const [correctlyWitnessed, setCorrectlyWitnessed] = useState(
    claim.correctlyWitnessed ?? false,
  );
  const [correctlyCompleted, setCorrectlyCompleted] = useState(
    claim.correctlyCompleted ?? false,
  );

  // The AI draft, once loaded into the fields above. Kept so the panel can
  // show what each proposal was based on — and so it is obvious which
  // values came from a model and still need checking.
  const [draft, setDraft] = useState<AdjudicationDraft | null>(null);

  const record = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.recoveries.publicMutations.recordAdjudication,
    ),
  });
  const draftWithAi = useMutation({
    mutationFn: useConvexAction(
      api.jobs.recoveries.adjudicationDraft.draftAdjudication,
    ),
  });

  async function handleDraft() {
    try {
      const result = (await draftWithAi.mutateAsync({
        claimId: claim._id,
      })) as AdjudicationDraft;
      setDocTypes(new Set(result.supportingDocTypes as PodSupportingDocType[]));
      setDocOther(result.supportingDocOther);
      setPodExecuted(result.podExecuted);
      setCorrectlyWitnessed(result.correctlyWitnessed);
      setCorrectlyCompleted(result.correctlyCompleted);
      if (result.groundsForRejection) setGrounds(result.groundsForRejection);
      setDraft(result);
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to draft with AI"));
    }
  }

  const admittedNum = parseFloat(admitted) || 0;
  const rejectedNum = parseFloat(rejected) || 0;
  const needsGrounds = rejectedNum > 0 && !grounds.trim();
  const splitMismatch =
    claim.claimedTotal > 0 &&
    Math.abs(admittedNum + rejectedNum - claim.claimedTotal) >= 0.005 &&
    (recommendation === "admit" || recommendation === "reject");
  const amountsRequired =
    recommendation === "admit" || recommendation === "reject";

  // A recommendation that the split flatly contradicts — "Admit" with
  // nothing admitted — is recorded on the claim and read back on the
  // adjudicated summary, where it disagrees with the outcome badge derived
  // from the amounts. Caught before it is written rather than explained after.
  const contradictsSplit =
    amountsRequired &&
    ((recommendation === "admit" && admittedNum <= 0) ||
      (recommendation === "reject" && rejectedNum <= 0));

  const blockingReason = !amountsRequired
    ? null
    : contradictsSplit
      ? recommendation === "admit"
        ? "Nothing is being admitted"
        : "Nothing is being rejected"
      : needsGrounds
        ? "Grounds are required to reject an amount"
        : null;

  function useSuggested() {
    setAdmitted(String(r.suggestedAdmit));
    setRejected(String(r.suggestedReject));
    setRecommendation(r.suggestedAdmit > 0 ? "admit" : "reject");
  }

  function toggleDocType(type: PodSupportingDocType) {
    setDocTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }

  async function handleRecord() {
    try {
      await record.mutateAsync({
        claimId: claim._id,
        recommendation,
        admittedAmount: amountsRequired ? admittedNum : 0,
        rejectedAmount: amountsRequired ? rejectedNum : 0,
        groundsForRejection: grounds.trim() || undefined,
        supportingDocTypes: Array.from(docTypes),
        supportingDocOther: docOther.trim() || undefined,
        podExecuted,
        correctlyWitnessed,
        correctlyCompleted,
      });
      toast.success("Adjudication recorded");
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to record adjudication"));
    }
  }

  const splitTotal = roundTo2(admittedNum + rejectedNum);
  const admitPct =
    claim.claimedTotal > 0
      ? Math.max(0, Math.min(100, (admittedNum / claim.claimedTotal) * 100))
      : 0;
  const rejectPct =
    claim.claimedTotal > 0
      ? Math.max(
          0,
          Math.min(100 - admitPct, (rejectedNum / claim.claimedTotal) * 100),
        )
      : 0;

  return (
    <div className="space-y-5">
      {/* What we are deciding on — carried from the reconciliation so the
          numbers are in view while the split is set. */}
      <div className="grid grid-cols-3 gap-3 rounded-xl border border-border bg-muted/30 p-4 text-sm">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Claimed
          </p>
          <p className="font-mono text-base font-semibold tabular-nums">
            {fmtMoney(claim.claimedTotal)}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Substantiated
          </p>
          <p className="font-mono text-base font-semibold tabular-nums">
            {fmtMoney(r.substantiatedTotal)}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Suggested
          </p>
          <p className="font-mono text-base font-semibold tabular-nums">
            <span className="text-emerald-700 dark:text-emerald-400">
              {fmtMoney(r.suggestedAdmit)}
            </span>
            {r.suggestedReject > 0 && (
              <span className="text-red-700 dark:text-red-400">
                {" / −"}
                {fmtMoney(r.suggestedReject)}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* The split */}
      <section className="rounded-xl border border-border p-4">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h3 className="text-sm font-bold">Determination</h3>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 text-xs"
            onClick={useSuggested}
          >
            Use suggested split
          </Button>
        </div>

        <div className="mb-4">
          <label className={LABEL_CLASS}>Recommendation</label>
          <Select
            value={recommendation}
            onValueChange={(v) => setRecommendation(v as PodRecommendation)}
          >
            <SelectTrigger className="text-sm sm:max-w-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(
                Object.entries(POD_RECOMMENDATION_LABELS) as Array<
                  [PodRecommendation, string]
                >
              ).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {contradictsSplit && (
            <p className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              {recommendation === "admit"
                ? "Nothing is being admitted, so this claim reads as rejected. Change the recommendation, or admit an amount below."
                : "Nothing is being rejected, so this claim reads as admitted. Change the recommendation, or reject an amount below."}
            </p>
          )}
        </div>

        {amountsRequired && (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 dark:border-emerald-900 dark:bg-emerald-950/30">
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-emerald-800 dark:text-emerald-300">
                  Admit for
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={admitted}
                  onChange={(e) => setAdmitted(e.target.value)}
                  className="h-11 border-emerald-300 bg-background font-mono text-lg font-semibold tabular-nums dark:border-emerald-800"
                />
                <p className="mt-1 text-[10px] text-emerald-800/70 dark:text-emerald-300/70">
                  Admitted for dividend purposes
                </p>
              </div>
              <div className="rounded-lg border border-red-200 bg-red-50/60 p-3 dark:border-red-900 dark:bg-red-950/30">
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-red-800 dark:text-red-300">
                  Reject for
                </label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={rejected}
                  onChange={(e) => setRejected(e.target.value)}
                  className="h-11 border-red-300 bg-background font-mono text-lg font-semibold tabular-nums dark:border-red-800"
                />
                <p className="mt-1 text-[10px] text-red-800/70 dark:text-red-300/70">
                  Disallowed — needs grounds below
                </p>
              </div>
            </div>

            {/* Proportion bar — the split at a glance */}
            {claim.claimedTotal > 0 && (
              <div className="mt-4">
                <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="bg-emerald-500 transition-all"
                    style={{ width: `${admitPct}%` }}
                  />
                  <div
                    className="bg-red-500 transition-all"
                    style={{ width: `${rejectPct}%` }}
                  />
                </div>
                <div className="mt-1.5 flex items-center justify-between text-[11px]">
                  <span className="text-muted-foreground">
                    Admitted + rejected{" "}
                    <span className="font-mono font-semibold tabular-nums text-foreground">
                      {fmtMoney(splitTotal)}
                    </span>{" "}
                    of {fmtMoney(claim.claimedTotal)} claimed
                  </span>
                  {splitMismatch ? (
                    <span className="flex items-center gap-1 font-medium text-amber-700 dark:text-amber-400">
                      <AlertTriangle className="h-3 w-3" />
                      Does not balance
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 font-medium text-emerald-700 dark:text-emerald-400">
                      <CheckCircle2 className="h-3 w-3" />
                      Balances
                    </span>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {amountsRequired && rejectedNum > 0 && (
          <div className="mt-4">
            <label className={LABEL_CLASS}>
              Grounds for Disallowance (Form 537) *
            </label>
            <Textarea
              value={grounds}
              onChange={(e) => setGrounds(e.target.value)}
              rows={3}
              placeholder="e.g. Insufficient evidence was provided to substantiate the full amount claimed; the supporting documents disclose lower late fees and legal fees than claimed."
            />
            <p className="mt-1 text-[10px] text-muted-foreground">
              This text goes on the Form 537 notice served on the creditor.
            </p>

            <DraftBasis text={draft?.groundsBasis} />
          </div>
        )}

        {/* Drafting aid. It fills the checklist and form checks below — and
            the Form 537 wording where an amount is being rejected — from
            what has already been extracted. Available on every outcome, not
            just rejections (MOR-889): the documentation checklist needs
            filling however the claim is decided. The determination and the
            admit / reject split stay with the practitioner. */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <p className="text-[10px] text-muted-foreground">
            {nothingToDraftFrom
              ? "Morgan drafts from the claim particulars. Add the lines from the creditor's POD form, or attach their documents, on the Claim & Evidence tab first."
              : `Morgan can draft the documentation checklist, the form checks${
                  amountsRequired && rejectedNum > 0
                    ? ", and the Form 537 wording"
                    : ""
                } from the extracted figures.`}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 gap-1.5 text-xs"
            disabled={draftWithAi.isPending || nothingToDraftFrom}
            title={
              nothingToDraftFrom
                ? "Nothing has been extracted from this claim yet — add the claim particulars or attach the creditor's POD documents first"
                : undefined
            }
            onClick={handleDraft}
          >
            {draftWithAi.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Wand2 className="h-3.5 w-3.5" />
            )}
            {draftWithAi.isPending
              ? "Drafting…"
              : draft
                ? "Re-draft with AI"
                : "Draft with AI"}
          </Button>
        </div>

        {draft && (
          <div className="mt-2 rounded-lg border border-violet-200 bg-violet-50/60 p-2.5 text-[10px] dark:border-violet-900 dark:bg-violet-950/30">
            <p className="font-semibold text-violet-900 dark:text-violet-200">
              AI draft loaded — check every value before recording.
            </p>
            <p className="mt-1 text-violet-900/70 dark:text-violet-200/70">
              Drafted from the extracted figures only; the model did not read
              the documents. The admitted / rejected split was not touched.
            </p>
          </div>
        )}
      </section>

      {/* Form checks */}
      <section className="rounded-xl border border-border p-4">
        <h3 className="mb-3 text-sm font-bold">Documentation &amp; Checks</h3>

        <p className={LABEL_CLASS}>Supporting documentation received</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {(
            Object.entries(POD_SUPPORTING_DOC_LABELS) as Array<
              [PodSupportingDocType, string]
            >
          ).map(([value, label]) => (
            <label
              key={value}
              className="flex cursor-pointer items-center gap-2 rounded-md border border-border px-2.5 py-2 text-xs transition-colors hover:bg-muted/50"
            >
              <Checkbox
                checked={docTypes.has(value)}
                onCheckedChange={() => toggleDocType(value)}
              />
              {label}
            </label>
          ))}
        </div>
        {docTypes.has("other") && (
          <Input
            className="mt-2 h-9 text-xs"
            value={docOther}
            onChange={(e) => setDocOther(e.target.value)}
            placeholder="Describe other supporting documentation"
          />
        )}
        <DraftBasis text={draft?.supportingDocBasis} />

        <p className={cn(LABEL_CLASS, "mt-4")}>Form checks</p>
        <div className="grid gap-2 sm:grid-cols-3">
          {(
            [
              ["POD executed", podExecuted, setPodExecuted],
              [
                "Correctly witnessed",
                correctlyWitnessed,
                setCorrectlyWitnessed,
              ],
              [
                "Correctly completed",
                correctlyCompleted,
                setCorrectlyCompleted,
              ],
            ] as Array<[string, boolean, (v: boolean) => void]>
          ).map(([label, value, setter]) => (
            <label
              key={label}
              className="flex cursor-pointer items-center gap-2 rounded-md border border-border px-2.5 py-2 text-xs transition-colors hover:bg-muted/50"
            >
              <Checkbox
                checked={value}
                onCheckedChange={(c) => setter(c === true)}
              />
              {label}
            </label>
          ))}
        </div>
        <DraftBasis text={draft?.checksBasis} />
      </section>

      <div className="flex items-center justify-end gap-3 pb-2">
        {blockingReason && (
          <span className="text-xs text-amber-700 dark:text-amber-400">
            {blockingReason}
          </span>
        )}
        <Button
          size="lg"
          disabled={
            record.isPending ||
            blockingReason !== null ||
            (amountsRequired && admittedNum < 0)
          }
          onClick={handleRecord}
        >
          {record.isPending ? "Recording…" : "Record Adjudication"}
        </Button>
      </div>
    </div>
  );
}

/**
 * The model's stated reason for what it put in the field above, so the
 * practitioner is checking a proposal rather than trusting a filled box.
 */
function DraftBasis({ text }: { text?: string }) {
  if (!text) return null;
  return (
    <p className="mt-2 flex items-start gap-1.5 rounded-md bg-violet-50 px-2.5 py-2 text-[11px] leading-relaxed text-violet-900/80 dark:bg-violet-950/40 dark:text-violet-200/80">
      <Wand2 className="mt-0.5 h-3 w-3 shrink-0" />
      <span>{text}</span>
    </p>
  );
}

// ---------------------------------------------------------------------------
// Adjudicated summary
// ---------------------------------------------------------------------------

function AdjudicatedSummary({
  detail,
  onReopen,
  onOpenContact,
}: {
  detail: PodClaimDetailUI;
  onReopen: () => void;
  /** Opens the creditor's contact record to fix a missing address. */
  onOpenContact: () => void;
}) {
  const { claim } = detail;
  const outcome = derivePodOutcome(claim);

  // Outcome email to the creditor (MOR-889). Morgan drafts it into the
  // mailbox review queue; the panel mirrors where it is in that flow.
  const [emailRequested, setEmailRequested] = useState(false);
  const { data: pendingDrafts } = useQuery(
    convexQuery(
      api.jobs.recoveries.adjudicationEmail.listPendingAdjudicationDrafts,
      { jobId: claim.jobId },
    ),
  );
  const hasPendingDraft = (pendingDrafts ?? []).some(
    (d) => d.podClaimId === claim._id,
  );
  const requestEmail = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.recoveries.adjudicationEmail.requestAdjudicationEmail,
    ),
  });

  // Whether the notice can be drafted at all, read up front from the same
  // resolver the mutation uses. A missing address or a mailbox that was
  // never switched on is a setup gap with a fix, so it is explained here
  // with the way to fix it rather than thrown back as a failed action.
  const { data: contactLink, isPending: contactLinkLoading } = useQuery(
    convexQuery(api.jobs.recoveries.adjudicationEmail.getPodClaimContact, {
      claimId: claim._id,
    }),
  );
  const emailBlockedReason = !contactLink
    ? null
    : !contactLink.mailboxActive
      ? "This job has no active mailbox yet, so there is nowhere to put the draft. Turn the job mailbox on in Mail settings."
      : !contactLink.contact
        ? `${claim.creditorName} is not on the job's contacts list yet, so there is nobody to address the notice to.`
        : !contactLink.contact.email
          ? `No email address is recorded for ${contactLink.contact.displayName}. Add one on their contact record and Morgan can draft the notice.`
          : null;
  const noticeSentAt = [...claim.changeHistory]
    .reverse()
    .find((entry) => entry.action === "adjudication_notice_sent")?.timestamp;

  async function handleRequestEmail() {
    try {
      await requestEmail.mutateAsync({ claimId: claim._id });
      setEmailRequested(true);
      toast.success(
        "Morgan is drafting the outcome email — it will appear in Mail → For Review",
      );
    } catch (error) {
      toast.error(
        extractErrorMessage(error, "Failed to request the outcome email"),
      );
    }
  }

  return (
    <section className="rounded-xl border border-border bg-muted/30 p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-bold">Adjudication</h3>
        <div className="flex flex-wrap items-center gap-2">
          {hasPendingDraft ? (
            <Badge
              color="amber"
              variant="soft"
              size="sm"
              title="Morgan drafted the outcome email — open Mail → For Review to approve and send"
            >
              Email awaiting review in the mailbox
            </Badge>
          ) : emailRequested ? (
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Morgan is drafting the email…
            </span>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="gap-1 text-xs"
              disabled={
                requestEmail.isPending ||
                contactLinkLoading ||
                emailBlockedReason !== null
              }
              onClick={handleRequestEmail}
              title={
                emailBlockedReason ??
                "Morgan drafts the email informing the creditor of this outcome; it goes to the mailbox review queue before anything is sent"
              }
            >
              <Mail className="h-3.5 w-3.5" />
              Draft outcome email
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="gap-1 text-xs"
            onClick={onReopen}
          >
            <Undo2 className="h-3.5 w-3.5" />
            Re-open
          </Button>
        </div>
      </div>

      {emailBlockedReason && !hasPendingDraft && (
        <div className="mb-3 flex flex-wrap items-start gap-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 flex-1">{emailBlockedReason}</span>
          {contactLink?.mailboxActive && (
            <Button
              variant="outline"
              size="sm"
              className="h-6 shrink-0 text-[11px]"
              onClick={onOpenContact}
            >
              Open contact record
            </Button>
          )}
        </div>
      )}

      {noticeSentAt !== undefined && (
        <p className="mb-3 flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Outcome email sent to the creditor on {fmtTimestamp(noticeSentAt)}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {outcome && (
          <Badge color={outcome.color} variant="soft" size="sm">
            {outcome.label}
          </Badge>
        )}
        {claim.recommendation && (
          <span className="text-xs text-muted-foreground">
            Recommendation: {POD_RECOMMENDATION_LABELS[claim.recommendation]}
          </span>
        )}
      </div>

      <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Claimed
          </p>
          <p className="font-mono font-semibold tabular-nums">
            {fmtMoney(claim.claimedTotal)}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Admitted
          </p>
          <p className="font-mono font-semibold tabular-nums text-emerald-700 dark:text-emerald-400">
            {fmtMoney(claim.admittedAmount ?? 0)}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Rejected
          </p>
          <p className="font-mono font-semibold tabular-nums text-red-700 dark:text-red-400">
            {fmtMoney(claim.rejectedAmount ?? 0)}
          </p>
        </div>
      </div>

      {claim.groundsForRejection && (
        <div className="mt-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">
            Grounds for Disallowance
          </p>
          <p className="mt-1 text-xs leading-relaxed">
            {claim.groundsForRejection}
          </p>
        </div>
      )}

      <p className="mt-3 text-xs text-muted-foreground">
        Adjudicated by {claim.adjudicatedBy ?? "—"} on{" "}
        {fmtTimestamp(claim.adjudicatedAt)}. The creditor has 14 days from
        service of the Form 537 notice to appeal any rejection.
      </p>
    </section>
  );
}
